import { computed } from 'vue'
import type { ComputedRef, Ref } from 'vue'

export const DEFAULT_BLOG_PAGE_SIZE = 10

type MaybeRef<T> = Ref<T> | ComputedRef<T>

function toPositiveInteger(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.floor(value) > 0 ? Math.floor(value) : null
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return null
    if (!/^\d+$/.test(trimmed)) return null
    const parsed = Number.parseInt(trimmed, 10)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  }

  return null
}

export function parsePageQueryParam(value: unknown): number {
  if (Array.isArray(value)) {
    return parsePageQueryParam(value[0])
  }

  return toPositiveInteger(value) ?? 1
}

export interface UseBlogPaginationOptions {
  pageQuery: MaybeRef<unknown>
  pageSize?: number
}

export function useBlogPagination<T>(
  items: MaybeRef<T[]>,
  options: UseBlogPaginationOptions,
) {
  const pageSize = options.pageSize && options.pageSize > 0
    ? Math.floor(options.pageSize)
    : DEFAULT_BLOG_PAGE_SIZE

  const totalItems = computed(() => items.value.length)
  const totalPages = computed(() => Math.max(1, Math.ceil(totalItems.value / pageSize)))
  const requestedPage = computed(() => parsePageQueryParam(options.pageQuery.value))

  function clampPage(page: number): number {
    if (!Number.isFinite(page)) return 1
    return Math.min(Math.max(1, Math.floor(page)), totalPages.value)
  }

  const currentPage = computed(() => clampPage(requestedPage.value))
  const hasPrev = computed(() => currentPage.value > 1)
  const hasNext = computed(() => currentPage.value < totalPages.value)
  const pageNumbers = computed(() => Array.from({ length: totalPages.value }, (_, index) => index + 1))

  const pagedItems = computed(() => {
    const start = (currentPage.value - 1) * pageSize
    return items.value.slice(start, start + pageSize)
  })

  return {
    pageSize,
    currentPage,
    totalItems,
    totalPages,
    pagedItems,
    hasPrev,
    hasNext,
    pageNumbers,
    clampPage,
  }
}
